import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";

interface Props {
  article: Article;
  variant?: "feature" | "standard" | "compact";
}

export default function ArticleCard({ article, variant = "standard" }: Props) {
  const href = `/article/${article.id}`;

  if (variant === "compact") {
    return (
      <article className="group flex gap-4 py-4 border-b border-background-200 last:border-none">
        <Link
          to={href}
          className="shrink-0 w-24 h-20 md:w-28 md:h-20 overflow-hidden rounded-md bg-background-200"
        >
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 黑料每日`}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </Link>
        <div className="flex flex-col justify-center min-w-0">
          <span className="text-[11px] text-primary-600 font-medium mb-1">{article.category}</span>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
            <Link to={href}>{article.title}</Link>
          </h3>
          <p className="text-xs text-foreground-500 mt-1 line-clamp-1">{article.excerpt}</p>
        </div>
      </article>
    );
  }

  if (variant === "feature") {
    return (
      <article className="group relative overflow-hidden rounded-lg bg-background-100 border border-background-200">
        <Link to={href} className="block aspect-[16/9] overflow-hidden bg-background-200">
          <img
            src={article.cover}
            alt={`${article.title} 配图`}
            title={`${article.title} - 黑料每日`}
            className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          />
        </Link>
        <div className="p-5 md:p-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-700 text-[11px] font-medium">
              {article.category}
            </span>
            <time className="text-xs text-foreground-400" dateTime={article.date}>
              {article.date}
            </time>
          </div>
          <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug mb-3 group-hover:text-primary-600 transition-colors">
            <Link to={href}>{article.title}</Link>
          </h3>
          <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3">
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-4 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-user-3-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
              {article.readTime}
            </span>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col rounded-lg overflow-hidden bg-background-50 border border-background-200 hover:border-primary-300 transition-colors">
      <Link to={href} className="block aspect-[4/3] overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} 配图`}
          title={`${article.title} - 黑料每日`}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
        />
      </Link>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[11px] font-medium text-primary-600">{article.category}</span>
          <time className="text-[11px] text-foreground-400" dateTime={article.date}>
            {article.date}
          </time>
        </div>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 leading-snug mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
          <Link to={href}>{article.title}</Link>
        </h3>
        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
          {article.excerpt}
        </p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-[11px] text-foreground-500">
          <span>{article.author}</span>
          <span className="flex items-center gap-1">
            <i className="ri-time-line w-4 h-4 flex items-center justify-center" aria-hidden="true" />
            {article.readTime}
          </span>
        </div>
      </div>
    </article>
  );
}